import type { CurrencyCodes } from '@onevesthq/ov-enums';
import type { PortfolioSubAccount } from '@ovComponents/4-module/widgets/portfolio/components/interfaces';
import { currencySymbol } from '@utils/currency';
import { formatMoneyValue } from '@utils/moneyFormatting';

export type MultiCurrencyCashBreakdownEntry = {
  currency: CurrencyCodes;
  cashCents: number;
  expectedCashCents: number;
};

type SubAccountWithOriginalCash = Pick<PortfolioSubAccount, 'originalValues'>;

/** Sum native cash pools across sleeves when a sub-account has multiple originalValues. */
export const aggregateMultiCurrencyCashBreakdown = (
  subAccounts: readonly SubAccountWithOriginalCash[],
): MultiCurrencyCashBreakdownEntry[] => {
  const totals = new Map<
    CurrencyCodes,
    { cashCents: number; expectedCashCents: number }
  >();

  for (const subAccount of subAccounts) {
    for (const original of subAccount.originalValues ?? []) {
      const existing = totals.get(original.currency) ?? {
        cashCents: 0,
        expectedCashCents: 0,
      };

      totals.set(original.currency, {
        cashCents: existing.cashCents + (original.cashCents ?? 0),
        expectedCashCents:
          existing.expectedCashCents + (original.expectedCashCents ?? 0),
      });
    }
  }

  if (totals.size <= 1) {
    return [];
  }

  return [...totals.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([currency, amounts]) => ({
      currency,
      ...amounts,
    }));
};

export type MultiCurrencyCashBreakdownField = 'cashCents' | 'expectedCashCents';

export const formatMultiCurrencyCashBreakdownLines = (
  entries: readonly MultiCurrencyCashBreakdownEntry[],
  field: MultiCurrencyCashBreakdownField = 'cashCents',
): string[] => {
  if (entries.length <= 1) {
    return [];
  }

  return entries.map(({ currency, cashCents, expectedCashCents }) => {
    const cents = field === 'expectedCashCents' ? expectedCashCents : cashCents;
    const symbol = currencySymbol[currency] ?? '$';

    return `${currency} ${formatMoneyValue(cents, symbol, 2, false)}`;
  });
};

export const formatMultiCurrencyCashBreakdownLabel = (
  entries: readonly MultiCurrencyCashBreakdownEntry[],
  field: MultiCurrencyCashBreakdownField = 'cashCents',
): string | undefined => {
  const lines = formatMultiCurrencyCashBreakdownLines(entries, field);

  if (lines.length <= 1) {
    return undefined;
  }

  return lines.join(' · ');
};
